import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { BookOpen, Sparkles } from 'lucide-react';

interface CreateDiscussionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'prompt' | 'chapter';
  onSubmit: (data: {
    type: 'prompt' | 'chapter';
    title: string;
    description?: string;
    chapter_start?: number;
    chapter_end?: number;
    is_spoiler?: boolean;
  }) => Promise<void>;
}

export function CreateDiscussionDialog({
  open,
  onOpenChange,
  type,
  onSubmit,
}: CreateDiscussionDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [chapterStart, setChapterStart] = useState('');
  const [chapterEnd, setChapterEnd] = useState('');
  const [isSpoiler, setIsSpoiler] = useState(type === 'chapter');
  const [submitting, setSubmitting] = useState(false);

  const isChapter = type === 'chapter';

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        chapter_start: chapterStart ? parseInt(chapterStart) : undefined,
        chapter_end: chapterEnd ? parseInt(chapterEnd) : undefined,
        is_spoiler: isSpoiler,
      });
      // Reset
      setTitle('');
      setDescription('');
      setChapterStart('');
      setChapterEnd('');
      setIsSpoiler(type === 'chapter');
      onOpenChange(false);
    } catch {
      // Error handled by hook
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isChapter ? (
              <>
                <BookOpen className="w-5 h-5 text-secondary" />
                Create Chapter Thread
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 text-secondary" />
                Post Discussion Prompt
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isChapter && (
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="chapter-start">From Chapter</Label>
                <Input
                  id="chapter-start"
                  type="number"
                  min={1}
                  value={chapterStart}
                  onChange={(e) => setChapterStart(e.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="chapter-end">To Chapter</Label>
                <Input
                  id="chapter-end"
                  type="number"
                  min={1}
                  value={chapterEnd}
                  onChange={(e) => setChapterEnd(e.target.value)}
                  placeholder="3"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="title">
              {isChapter ? 'Thread Title' : 'Prompt Question'}
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                isChapter
                  ? 'e.g. Chapters 1–3: First Impressions'
                  : 'e.g. What surprised you most about the ending?'
              }
            />
          </div>

          <div>
            <Label htmlFor="description">
              {isChapter ? 'Discussion guidance (optional)' : 'Additional context (optional)'}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isChapter
                  ? 'Any specific points to discuss in these chapters...'
                  : 'Add more context or sub-questions...'
              }
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="spoiler-toggle" className="text-sm font-medium">
                Spoiler warning
              </Label>
              <p className="text-xs text-muted-foreground">
                Collapse by default to hide spoilers
              </p>
            </div>
            <Switch
              id="spoiler-toggle"
              checked={isSpoiler}
              onCheckedChange={setIsSpoiler}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || submitting}>
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
